import { AlertTriangle, Check, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import {
  ImportResult,
  parseImportFile,
  runWithConcurrency,
  useImportGrammar,
  useImportQuiz,
  useImportScenarios,
  useImportWords,
  type ImportKind,
} from "@/lib/admin";
import { cn } from "@/lib/utils";

// In-flight cap for parallel uploads. Each admin import does many DB
// round-trips (vocab especially: ~30 queries × N words inside one
// transaction), and shared-tag upserts contend on row locks. Cap at 3
// to balance throughput against contention — a sweep of 60 sample
// files completes in ~60–90s vs. ~3 min sequential, without the
// thundering-herd problems of unbounded `Promise.all`.
const CONCURRENCY = 3;

type FileState =
  | { kind: "queued" }
  | { kind: "parsing" }
  | { kind: "uploading" }
  | { kind: "success"; result: ImportResult }
  | { kind: "error"; error: string };

type FileEntry = {
  // Stable id so React's reconciliation across status updates is clean.
  // File objects don't have ids; index isn't stable if files are re-added.
  id: string;
  file: File;
  state: FileState;
};

type DropZoneProps = {
  title: string;
  hint: string;
  schema: string;
  fileKind: ImportKind;
  onUpload: (payload: unknown) => Promise<ImportResult>;
};

const VOCAB_SCHEMA = `{
  "defaultTags": ["a1"],
  "words": [
    {
      "lemma": "a citi",
      "language": "RO",
      "pos": "VERB",
      "pronunciation": "[a tʃiˈti]",
      "translations": ["читать"],
      "synonyms": ["a parcurge"],
      "examples": [
        { "text": "Îmi place să citesc.",
          "translation": "Мне нравится читать." }
      ],
      "tags": ["daily"]
    }
  ]
}`;

const GRAMMAR_SCHEMA = `{
  "defaultTags": ["cases"],
  "rules": [
    {
      "title": "Cazul dativ",
      "body": "# Cazul dativ\\n\\nMarkdown body...",
      "tags": ["nouns"]
    }
  ]
}`;

const QUIZ_SCHEMA = `{
  "ruleSlug": "verb-present-general",
  "defaultTags": ["a1"],
  "items": [
    {
      "slug": "verb-present-general-q1",
      "kind": "CLOZE",
      "sentence": "Eu ___ o carte.",
      "answers": ["citesc"],
      "translation": "Я читаю книгу.",
      "distractors": ["citește", "citesti"],
      "hints": ["a citi", "1st sg."]
    }
  ]
}`;

const SCENARIO_SCHEMA = `{
  "slug": "intro-yourself",
  "title": "Представиться",
  "level": "A1",
  "stage": 3,
  "capstone": false,
  "promise": "Сказать, как тебя зовут, откуда ты, и сколько тебе лет.",
  "estimatedMinutes": 25,
  "wordLemmas": ["nume", "prenume", "salut"],
  "ruleSlugs": ["verb-a-fi", "pronoun-personal-nominative"],
  "constructions": [
    { "ro": "Mă numesc Andrei.", "ru": "Меня зовут Андрей." }
  ],
  "checkpointQuizSlug": "scenario-intro-yourself-checkpoint",
  "completionCriteria": {
    "sessionStepsCompleted": "all",
    "checkpointPassed": true
  },
  "prerequisiteScenarios": [],
  "tags": ["smalltalk", "first-meeting"]
}`;

let nextEntryId = 0;
function newEntry(file: File): FileEntry {
  return { id: `${nextEntryId++}-${file.name}`, file, state: { kind: "queued" } };
}

function DropZone({ title, hint, schema, fileKind, onUpload }: DropZoneProps) {
  const [entries, setEntries] = useState<FileEntry[]>([]);
  const [dragging, setDragging] = useState(false);
  const [running, setRunning] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Functional update keeps the per-file status changes correct under
  // concurrent worker callbacks — workers don't see each other's state.
  function setEntryState(id: string, state: FileState) {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, state } : e)));
  }

  async function processOne(entry: FileEntry): Promise<void> {
    setEntryState(entry.id, { kind: "parsing" });
    const parsed = await parseImportFile(entry.file, fileKind);
    if (!parsed.ok) {
      setEntryState(entry.id, { kind: "error", error: parsed.error });
      return;
    }
    setEntryState(entry.id, { kind: "uploading" });
    try {
      const result = await onUpload(parsed.payload);
      setEntryState(entry.id, { kind: "success", result });
    } catch (err) {
      setEntryState(entry.id, {
        kind: "error",
        error: err instanceof Error ? err.message : String(err),
      });
    }
  }

  async function ingest(files: FileList | File[]) {
    const fresh = Array.from(files).map(newEntry);
    if (fresh.length === 0) return;
    setEntries((prev) => [...prev, ...fresh]);
    setRunning(true);
    try {
      await runWithConcurrency(fresh, CONCURRENCY, processOne);
    } finally {
      setRunning(false);
    }
  }

  function clearAll() {
    setEntries([]);
  }

  const totals = aggregate(entries);
  const hasItems = entries.length > 0;

  // Border + bg track the dominant state — gives at-a-glance signal
  // without obscuring per-file status below.
  const borderColor = dragging
    ? "border-[color:var(--accent)]"
    : totals.failed > 0
      ? "border-[color:var(--again)]"
      : hasItems && totals.done === entries.length && !running
        ? "border-[color:var(--good)]"
        : "border-border-strong";

  const bgColor = dragging
    ? "bg-[color:var(--accent-soft)]"
    : totals.failed > 0
      ? "bg-[color:var(--again-soft)]"
      : hasItems && totals.done === entries.length && !running
        ? "bg-[color:var(--good-soft)]"
        : "bg-surface";

  return (
    <div className="overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface">
      {/* Header */}
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-baseline justify-between">
          <div className="text-[15px] font-semibold">{title}</div>
          <span className="rounded-full border border-border bg-[color:var(--surface-2)] px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider text-text-3">
            JSON
          </span>
        </div>
        <div className="mt-0.5 text-[12px] text-text-3">{hint}</div>
      </div>

      {/* Dropzone — always visible so users can drop more files mid-run */}
      <label
        className={cn(
          "m-3 block cursor-pointer rounded-[var(--r-md)] border-2 border-dashed p-5 text-center transition-colors",
          borderColor,
          bgColor,
          running && "cursor-progress opacity-80",
        )}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (e.dataTransfer.files.length > 0) ingest(e.dataTransfer.files);
        }}
      >
        <input
          ref={inputRef}
          type="file"
          accept="application/json,.json"
          multiple
          className="sr-only"
          onChange={(e) => {
            if (e.target.files && e.target.files.length > 0) ingest(e.target.files);
            e.target.value = ""; // allow re-selecting the same files
          }}
        />
        <IdleHint multiple />
      </label>

      {/* File list — appears after first drop. Per-file rows give
          row-level pass/fail without the user having to mentally
          aggregate a single status indicator across many files. */}
      {hasItems && (
        <div className="border-t border-border">
          <SummaryBar
            total={entries.length}
            done={totals.done}
            failed={totals.failed}
            running={running}
            onClear={clearAll}
          />
          <ul className="max-h-72 divide-y divide-border overflow-auto">
            {entries.map((entry) => (
              <FileRow key={entry.id} entry={entry} />
            ))}
          </ul>
        </div>
      )}

      {/* Schema preview */}
      <div className="px-4 pb-3 pt-3">
        <details>
          <summary className="cursor-pointer select-none py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-text-3">
            Expected schema
          </summary>
          <pre className="mt-2 overflow-auto rounded-[var(--r-sm)] border border-border bg-[color:var(--surface-2)] p-3 font-mono text-[10px] leading-relaxed text-text-2">
            {schema}
          </pre>
        </details>
      </div>
    </div>
  );
}

function aggregate(entries: FileEntry[]) {
  let done = 0;
  let failed = 0;
  for (const e of entries) {
    if (e.state.kind === "success") done++;
    else if (e.state.kind === "error") {
      done++;
      failed++;
    }
  }
  return { done, failed };
}

function IdleHint({ multiple }: { multiple: boolean }) {
  return (
    <>
      <div className="mx-auto mb-2 flex size-11 items-center justify-center rounded-xl bg-[color:var(--accent-soft)] text-[color:var(--accent)]">
        <Upload className="size-5" strokeWidth={1.8} />
      </div>
      <div className="text-[14px] font-semibold text-text">Drop {multiple ? ".json files" : ".json file"}</div>
      <div className="mt-1 text-[12px] text-text-3">
        or <span className="font-medium text-[color:var(--accent)]">browse</span>
        {multiple && " — multi-select supported"}
      </div>
    </>
  );
}

function SummaryBar({
  total,
  done,
  failed,
  running,
  onClear,
}: {
  total: number;
  done: number;
  failed: number;
  running: boolean;
  onClear: () => void;
}) {
  const pct = total > 0 ? (done / total) * 100 : 0;
  const ok = done - failed;
  return (
    <div className="space-y-2 px-4 py-3">
      <div className="flex items-center justify-between text-[12px]">
        <div className="font-mono text-text-2 tabular-nums">
          {done}/{total} processed
          {ok > 0 && <span className="ml-2 text-[color:var(--good)]">{ok} ok</span>}
          {failed > 0 && <span className="ml-2 text-[color:var(--again)]">{failed} failed</span>}
          {running && <span className="ml-2 text-text-3">…running</span>}
        </div>
        {!running && (
          <button
            type="button"
            onClick={onClear}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium text-text-3 hover:bg-[color:var(--surface-2)] hover:text-text"
          >
            <X className="size-3" />
            Clear
          </button>
        )}
      </div>
      {/* Progress bar — done/total. Visual reinforcement of the count. */}
      <div className="h-[3px] w-full overflow-hidden rounded-full bg-[color:var(--surface-2)]">
        <div
          className="h-full transition-[width] duration-200"
          style={{
            width: `${pct}%`,
            background: failed > 0 ? "var(--again)" : "var(--good)",
          }}
        />
      </div>
    </div>
  );
}

function FileRow({ entry }: { entry: FileEntry }) {
  const { file, state } = entry;
  return (
    <li className="flex items-start gap-3 px-4 py-2.5">
      <StatusBadge state={state} />
      <div className="min-w-0 flex-1">
        <div className="truncate font-mono text-[12px] text-text">{file.name}</div>
        <div className="mt-0.5 text-[11px] text-text-3">{stateLabel(state)}</div>
      </div>
    </li>
  );
}

function stateLabel(state: FileState): string {
  switch (state.kind) {
    case "queued":
      return "Queued";
    case "parsing":
      return "Validating…";
    case "uploading":
      return "Uploading…";
    case "success": {
      const r = state.result;
      const errSuffix = r.errors.length > 0 ? ` · ${r.errors.length} item failed` : "";
      // Surface non-fatal warnings — most useful for scenarios where
      // soft refs (unknown lemma / unknown rule slug / unknown
      // prerequisite) fire here.
      const warnSuffix = r.warnings && r.warnings.length > 0 ? ` · ${r.warnings.length} warning` : "";
      return `+${r.added} added · ~${r.updated} updated${errSuffix}${warnSuffix}`;
    }
    case "error":
      return state.error;
  }
}

function StatusBadge({ state }: { state: FileState }) {
  const base = "flex size-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold mt-0.5";
  switch (state.kind) {
    case "queued":
      return <div className={cn(base, "bg-[color:var(--surface-2)] text-text-3")}>•</div>;
    case "parsing":
    case "uploading":
      return (
        <div
          className={cn(base, "animate-pulse bg-[color:var(--accent-soft)] text-[color:var(--accent)]")}
          aria-label={state.kind}
        >
          ⟳
        </div>
      );
    case "success":
      return (
        <div className={cn(base, "bg-[color:var(--good)] text-white")} aria-label="success">
          <Check className="size-3" strokeWidth={3} />
        </div>
      );
    case "error":
      return (
        <div className={cn(base, "bg-[color:var(--again)] text-white")} aria-label="error">
          !
        </div>
      );
  }
}

export function AdminPage() {
  const words = useImportWords();
  const grammar = useImportGrammar();
  const quiz = useImportQuiz();
  const scenarios = useImportScenarios();

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h1 className="text-[26px] font-bold tracking-[-0.02em]">Admin</h1>
        <div className="mt-0.5 text-[13px] text-text-3">
          Bulk-import vocabulary, grammar, and quizzes (drop a folder)
        </div>
      </div>

      <DropZone
        title="Vocabulary"
        hint="Words, translations, examples, tags."
        schema={VOCAB_SCHEMA}
        fileKind="words"
        onUpload={(payload) => words.mutateAsync(payload)}
      />

      <DropZone
        title="Grammar"
        hint="Rules, markdown body, tags."
        schema={GRAMMAR_SCHEMA}
        fileKind="grammar"
        onUpload={(payload) => grammar.mutateAsync(payload)}
      />

      {/* Quiz zone — same UX as the others. Each file targets ONE rule
          (via top-level `ruleSlug`); multiple files = multiple rules.
          The grammar zone must be fed first if quizzes reference rules
          that don't yet exist (importer rejects unknown rule slugs). */}
      <DropZone
        title="Quizzes"
        hint="Per-rule quiz items (CLOZE / TRANSFORM). Run after Grammar."
        schema={QUIZ_SCHEMA}
        fileKind="quiz"
        onUpload={(payload) => quiz.mutateAsync(payload)}
      />

      {/* Scenarios zone — runs last because each scenario soft-refs
          vocab, grammar, and (for checkpoints) quiz items. Soft refs
          warn-not-fail, so you CAN drag scenarios first while iterating
          on a content batch — the row will show a "warnings" suffix and
          re-import after the deps land clears them. */}
      <DropZone
        title="Scenarios"
        hint="Capability scenarios (one per file). Run after Vocab + Grammar + Quizzes."
        schema={SCENARIO_SCHEMA}
        fileKind="scenarios"
        onUpload={(payload) => scenarios.mutateAsync(payload)}
      />

      {/* Warning strip */}
      <div className="flex items-start gap-3 rounded-[var(--r-md)] border border-[color:oklch(85%_0.08_60)] bg-[color:oklch(97%_0.025_60)] p-3 text-text">
        <div className="mt-0.5 flex size-5 flex-shrink-0 items-center justify-center rounded-full bg-[color:var(--hard)] text-white">
          <AlertTriangle className="size-3" />
        </div>
        <div className="text-[12px] leading-relaxed text-[color:oklch(35%_0.08_60)]">
          Imports <b>merge by lemma (words), slug/title (grammar), or slug (quizzes / scenarios)</b>. Entries not in the
          file are kept — this is additive. Files run with up to {CONCURRENCY} in flight; back up your DB before large
          imports.
        </div>
      </div>
    </div>
  );
}
